import { 
    Box, 
    Divider, 
    HStack, 
    Spinner,
    Text, 
    VStack,
    Image,
  } from "@chakra-ui/react"; 
  import ReactMarkdown from "react-markdown";
  
  interface MessageProps {
    author: "user" | "ai";
    content: string;
    isLoading: boolean;
    isLastMessage: boolean;
  }
  
  const MessageComponent: React.FC<MessageProps> = ({
    author,
    content,
    isLoading,
    isLastMessage,
  }) => {
  
    return (
      <HStack
        alignSelf={author === "user" ? "flex-end" : "flex-start"} 
        w={author === "user" ? "auto" : "95%"}
      >
        <Box
          bg={author === "user" ? "purple.50" : "gray.50"}
          p={3}
          borderRadius="lg"
          maxW="100%"
          flex="1"
          ml={author === "user" ? "auto" : "0"}
          mb={4}
        >
          {isLoading && isLastMessage ? (
            // Loading state
            <VStack align="start" spacing={2}>
              <HStack w="100%" justify="space-between">
                <HStack>
                  <Image 
                    src="/Anko.png"
                    alt="Anko"
                    boxSize={6}
                    mr={2}
                    objectFit="contain"
                  />
                  <Box display="flex" alignItems="center" minH="24px">
                    <Spinner size="sm" color="purple.700" mr={2} />
                    <Text fontSize="sm" color="gray.500" mb={0}>
                      Thinking ...
                    </Text>
                  </Box>
                </HStack>
              </HStack>
            </VStack>
          ) : (
            // Normal message state
            <VStack align="start" spacing={1} w="100%">
              { author === "ai" && (
                <VStack w="100%">
                  <HStack w="100%" justify="space-between">
                    <HStack>
                      <Image 
                        src="/Anko.png"
                        alt="Anko"
                        boxSize={6}
                        mr={2}
                        objectFit="contain"
                        sx={{
                          "@keyframes spin": {
                            "0%": { transform: "rotate(0deg)" },
                            "50%": { transform: "rotate(360deg)" },
                            "100%": { transform: "rotate(360deg)" },
                          },
                        }}
                        _groupHover={{
                          animation: "spin 4s linear infinite",
                        }}
                      />
                    </HStack>
                  </HStack>
                  <Divider my={0} borderColor="gray.300" />
                </VStack>
              )}
              { author === "ai" && (
                <VStack align="start" spacing={1} w="100%">
                  <Box textAlign="left" ml={0} className="markdown-content">
                    <ReactMarkdown
                      components={{
                        // Add custom components for markdown elements
                        ul: ({node, ...props}) => (
                          <ul style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                        ),
                        ol: ({node, ...props}) => (
                          <ol style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                        ),
                        li: ({node, ...props}) => (
                          <li style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }} {...props} />
                        ),
                        p: ({node, ...props}) => (
                          <p style={{ fontSize: "0.875rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                        ),
                        h1: ({node, ...props}) => (
                          <h1 style={{ fontSize: "1.15rem", fontWeight: "bold", margin: "1rem 0" }} {...props} />
                        ),
                        h2: ({node, ...props}) => (
                          <h2 style={{ fontSize: "1.05rem", fontWeight: "bold", margin: "0.875rem 0" }} {...props} />
                        ),
                        h3: ({node, ...props}) => (
                          <h3 style={{ fontSize: "0.95rem", fontWeight: "bold", margin: "0.75rem 0" }} {...props} />
                        ),
                        h4: ({node, ...props}) => (
                          <h4 style={{ fontSize: "0.85rem", fontWeight: "bold", margin: "0.625rem 0" }} {...props} />
                        ),
                      }}
                    >
                      {content}
                    </ReactMarkdown>
                  </Box>
                </VStack>
              )}
              { author === "user" && (
                <Text mb={0} alignSelf="flex-end" fontSize="sm" textAlign="left">
                  {content }
                </Text>
              )}
            </VStack>
          )}
        </Box>
      </HStack>
    );
  };
  
  export default MessageComponent;